import { useNavigate } from "react-router-dom"
import { Plus, LayoutList, Kanban } from "lucide-react"
import { useServiceTicketsBoard } from "@/hooks/use-service-tickets"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import type { ServiceTicket } from "@/lib/types"

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  low: { label: "Niski", color: "#6B7280" },
  medium: { label: "Średni", color: "#3B82F6" },
  high: { label: "Wysoki", color: "#F59E0B" },
  urgent: { label: "Pilny", color: "#EF4444" },
}

function TicketCard({ ticket }: { ticket: ServiceTicket }) {
  const navigate = useNavigate()
  const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium

  return (
    <div
      onClick={() => navigate(`/service/${ticket.id}`)}
      className="cursor-pointer rounded-lg border border-border p-3 transition-all hover:shadow-sm bg-card"
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-sm font-medium leading-tight text-foreground">
          {ticket.title}
        </h4>
        <Badge variant="outline" className="flex-shrink-0 text-[9px]" style={{ borderColor: priorityConfig.color, color: priorityConfig.color }}>
          {priorityConfig.label}
        </Badge>
      </div>
      {ticket.company && (
        <p className="mt-1 text-xs text-muted-foreground">
          {ticket.company.name}
        </p>
      )}
      <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
        {ticket.assigned_to && (
          <span>{ticket.assigned_to.first_name} {ticket.assigned_to.last_name}</span>
        )}
        {ticket.days_in_phase > 0 && (
          <span>{ticket.days_in_phase}d</span>
        )}
      </div>
    </div>
  )
}

export function ServiceTicketsBoardPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useServiceTicketsBoard()
  const columns = data?.data ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-foreground">
            Serwis
          </h1>
          <div className="flex items-center rounded-md border border-input overflow-hidden">
            <button
              onClick={() => navigate("/service")}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors text-muted-foreground"
            >
              <LayoutList size={14} />
              Lista
            </button>
            <button className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium transition-colors bg-primary text-primary-foreground">
              <Kanban size={14} />
              Tablica
            </button>
          </div>
        </div>
        <Button onClick={() => navigate("/service/new")}>
          <Plus size={16} /> Nowe zgłoszenie
        </Button>
      </div>

      {isLoading ? (
        <div className="flex gap-4 overflow-x-auto">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-96 w-72 flex-shrink-0 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: 500 }}>
          {columns.map((col) => (
            <div
              key={col.phase.id}
              className="flex w-72 flex-shrink-0 flex-col rounded-lg bg-muted"
            >
              {/* Column header */}
              <div className="flex items-center gap-2 px-3 py-2.5">
                <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: col.phase.color }} />
                <span className="text-sm font-medium text-foreground">
                  {col.phase.name}
                </span>
                <span
                  className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
                  style={{ backgroundColor: `${col.phase.color}20`, color: col.phase.color }}
                >
                  {col.count}
                </span>
              </div>

              {/* Cards */}
              <div className="flex-1 space-y-2 px-2 pb-2">
                {col.tickets.map((ticket) => (
                  <TicketCard key={ticket.id} ticket={ticket} />
                ))}
                {col.tickets.length === 0 && (
                  <div className="flex items-center justify-center rounded-lg border border-dashed border-border py-8 text-xs text-muted-foreground">
                    Brak zgłoszeń
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
